import { Controller } from "@hotwired/stimulus"

// Pipeline controller for drag and drop, filtering, and lead management functionality
export default class extends Controller {
  static targets = ["container", "column", "card", "cardContainer", "dropIndicator", "scrollLeftButton", "scrollRightButton", "mouseIndicatorTemplate"]
  
  // No outlets needed
  
  connect() {
    // Restore scroll position from previous session if available
    this.restoreScrollPosition()
    
    // Setup initial scroll button states
    if (this.hasContainerTarget) {
      this.updateScrollButtonStates()
    }
    
    // Initialize dragging state
    this.isDragging = false;
  }
  
  // Handle scroll events from the container
  handleScroll() {
    this.updateScrollButtonStates();
    
    // If dragging is active, update the button highlighting based on new scroll position
    if (this.isDragging) {
      this.updateButtonHighlighting();
    }
  }
  
  // Restore scroll position after page reload
  restoreScrollPosition() {
    // Get the saved scroll position from session storage
    const savedPosition = sessionStorage.getItem('pipelineScrollPosition');
    
    if (savedPosition !== null && this.hasContainerTarget) {
      // Initially set opacity to 0 to prevent flickering
      this.containerTarget.style.opacity = '0';
      
      // Use setTimeout to ensure the DOM is fully loaded
      setTimeout(() => {
        // Set the scroll position
        this.containerTarget.scrollLeft = parseInt(savedPosition, 10);
        console.log('Restored scroll position:', savedPosition);
        
        // Fade in the container
        this.containerTarget.style.transition = 'opacity 0.3s ease-in-out';
        this.containerTarget.style.opacity = '1';
        
        // Clear the saved position after restoring
        sessionStorage.removeItem('pipelineScrollPosition');
      }, 100);
    }
  }
  
  // Allow drop on pipeline columns
  allowDrop(event) {
    event.preventDefault();
    
    // Show the drop indicator for the current column
    const status = event.currentTarget.dataset.status;
    if (status) {
      // Find the drop indicator for this status
      this.dropIndicatorTargets.forEach(indicator => {
        if (indicator.dataset.status === status) {
          // Simply show the indicator without changing its position
          indicator.classList.remove('hidden');
        }
      });
    }
    
    // Add visual feedback for drop target
    event.currentTarget.classList.add('bg-indigo-50');
  }
  
  // Handle drag leave
  handleDragLeave(event) {
    // Remove visual feedback
    event.currentTarget.classList.remove('bg-indigo-50');
    
    // Hide the drop indicator for this column
    const status = event.currentTarget.dataset.status;
    if (status) {
      this.dropIndicatorTargets.forEach(indicator => {
        if (indicator.dataset.status === status) {
          indicator.classList.add('hidden');
        }
      });
    }
  }
  
  // Handle drag start
  handleDragStart(event) {
    try {
      // Get the lead ID and status from the dragged element
      const target = event.currentTarget;
      const leadId = target.dataset.id;
      const currentStatus = target.dataset.status;
      
      // Set the data transfer properties
      const data = { leadId, currentStatus };
      
      // Set the data in the drag event - use application/json format
      event.dataTransfer.setData('application/json', JSON.stringify(data));
      
      event.dataTransfer.effectAllowed = 'move';
      
      // Add visual effect to the dragged card
      target.classList.add('opacity-50');
      
      // Store current scroll position for restoration after page reload
      if (this.hasContainerTarget) {
        this.scrollPosition = this.containerTarget.scrollLeft;
      }
      
      // Set dragging state to true
      this.isDragging = true;
      
      // Update button highlighting based on current scroll position
      this.updateButtonHighlighting();
      
      console.log('Drag started:', data);
    } catch (error) {
      console.error('Error starting drag:', error);
    }
  }
  
  // Handle drag end
  handleDragEnd(event) {
    // Remove the visual effect from the dragged card
    event.currentTarget.classList.remove('opacity-50');
    
    // Remove the visual indicator from all columns
    this.columnTargets.forEach(column => {
      column.classList.remove('bg-indigo-50');
    });
    
    // Hide all drop indicators
    this.dropIndicatorTargets.forEach(indicator => {
      indicator.classList.add('hidden');
    });
    
    // Reset dragging state
    this.isDragging = false;
    
    // Reset scroll button highlighting
    this.resetScrollButtonHighlighting();
  }
  
  // Update scroll button states based on current scroll position
  updateScrollButtonStates() {
    if (!this.hasContainerTarget || !this.hasScrollLeftButtonTarget || !this.hasScrollRightButtonTarget) return;
    
    const currentScroll = this.containerTarget.scrollLeft;
    const maxScroll = this.containerTarget.scrollWidth - this.containerTarget.clientWidth;
    
    // Determine if we're at the beginning or end of the scroll
    const atBeginning = currentScroll <= 0;
    const atEnd = currentScroll >= maxScroll - 5; // Allow a small margin of error
    
    // Update left button state
    if (atBeginning) {
      this.scrollLeftButtonTarget.disabled = true;
      this.scrollLeftButtonTarget.classList.add('opacity-50');
    } else {
      this.scrollLeftButtonTarget.disabled = false;
      this.scrollLeftButtonTarget.classList.remove('opacity-50');
    }
    
    // Update right button state
    if (atEnd) {
      this.scrollRightButtonTarget.disabled = true;
      this.scrollRightButtonTarget.classList.add('opacity-50');
    } else {
      this.scrollRightButtonTarget.disabled = false;
      this.scrollRightButtonTarget.classList.remove('opacity-50');
    }
  }
  
  // Update button highlighting based on current scroll position
  updateButtonHighlighting() {
    if (!this.hasScrollLeftButtonTarget || !this.hasScrollRightButtonTarget || !this.isDragging) return;
    
    // First reset all highlighting
    this.resetScrollButtonHighlighting();
    
    // Then apply highlighting based on current scroll position
    const currentScroll = this.containerTarget.scrollLeft;
    const maxScroll = this.containerTarget.scrollWidth - this.containerTarget.clientWidth;
    
    // Determine if we're at the beginning or end of the scroll
    const atBeginning = currentScroll <= 0;
    const atEnd = currentScroll >= maxScroll - 5; // Allow a small margin of error
    
    // Highlight left button if not at beginning
    if (!atBeginning) {
      this.scrollLeftButtonTarget.classList.add('bg-yellow-100');
      this.scrollLeftButtonTarget.classList.add('animate-pulse');
      
      // Add mouse icon indicator if it doesn't exist
      this.addMouseIndicator(this.scrollLeftButtonTarget, 'left');
    }
    
    // Highlight right button if not at end
    if (!atEnd) {
      this.scrollRightButtonTarget.classList.add('bg-yellow-100');
      this.scrollRightButtonTarget.classList.add('animate-pulse');
      
      // Add mouse icon indicator if it doesn't exist
      this.addMouseIndicator(this.scrollRightButtonTarget, 'right');
    }
  }
  
  // Add mouse indicator to button
  addMouseIndicator(button, direction) {
    // Check if indicator already exists
    if (button.querySelector('.mouse-indicator')) return;
    
    // Get the template using Stimulus target instead of document.getElementById
    if (!this.hasMouseIndicatorTemplateTarget) return; // Template not found
    
    // Clone the template content
    const indicator = this.mouseIndicatorTemplateTarget.content.cloneNode(true).querySelector('.mouse-indicator');
    
    // Make sure button has position relative for absolute positioning
    if (window.getComputedStyle(button).position === 'static') {
      button.style.position = 'relative';
    }
    
    // Add to button
    button.appendChild(indicator);
    
    // Add a subtle entrance animation
    indicator.style.opacity = '0';
    setTimeout(() => {
      indicator.style.opacity = '1';
      indicator.style.transition = 'opacity 0.3s ease-in-out';
    }, 10);
    
    // Remove after 1.5 seconds to avoid cluttering the UI
    setTimeout(() => {
      if (indicator && indicator.parentNode) {
        indicator.style.opacity = '0';
        setTimeout(() => {
          if (indicator && indicator.parentNode) {
            indicator.parentNode.removeChild(indicator);
          }
        }, 300); // Wait for fade out animation
      }
    }, 1500);
  }
  
  // Reset scroll button highlighting
  resetScrollButtonHighlighting() {
    if (!this.hasScrollLeftButtonTarget || !this.hasScrollRightButtonTarget) return;
    
    this.scrollLeftButtonTarget.classList.remove('bg-yellow-100');
    this.scrollLeftButtonTarget.classList.remove('animate-pulse');
    
    this.scrollRightButtonTarget.classList.remove('bg-yellow-100');
    this.scrollRightButtonTarget.classList.remove('animate-pulse');
    
    // Remove any mouse indicators
    const indicators = document.querySelectorAll('.mouse-indicator');
    indicators.forEach(indicator => {
      if (indicator.parentNode) {
        indicator.parentNode.removeChild(indicator);
      }
    });
  }
  
  // Scroll to the beginning of the container
  scrollToStart() {
    if (!this.hasContainerTarget) return;
    
    this.containerTarget.scrollTo({
      left: 0,
      behavior: 'smooth'
    });
  }
  
  // Scroll to the end of the container
  scrollToEnd() {
    if (!this.hasContainerTarget) return;
    
    this.containerTarget.scrollTo({
      left: this.containerTarget.scrollWidth - this.containerTarget.clientWidth,
      behavior: 'smooth'
    });
  }
  
  // Handle drop on a column
  handleDrop(event) {
    event.preventDefault();
    
    // Remove the visual indicator from the drop target
    event.currentTarget.classList.remove('bg-indigo-50');
    
    // Hide all drop indicators
    this.dropIndicatorTargets.forEach(indicator => {
      indicator.classList.add('hidden');
    });
    
    try {
      // Get the data using application/json format
      let leadId, currentStatus;
      
      // Get the JSON data
      const jsonData = event.dataTransfer.getData("application/json");
      
      // Parse the JSON data
      if (jsonData) {
        try {
          const data = JSON.parse(jsonData);
          leadId = data.leadId;
          currentStatus = data.currentStatus;
        } catch (e) {
          console.error('Error parsing JSON data:', e);
        }
      }
      
      // If we still don't have the data, abort
      if (!leadId || !currentStatus) {
        console.error("Could not retrieve lead data from drop event");
        return;
      }
      
      const newStatus = event.currentTarget.dataset.status;
      
      console.log('Drop data:', { leadId, currentStatus, newStatus });
      
      // If the status hasn't changed, do nothing
      if (currentStatus === newStatus) {
        return;
      }
      
      // Find the card element
      const cardElement = this.cardTargets.find(card => card.dataset.id === leadId);
      if (!cardElement) {
        console.error("Could not find card element");
        return;
      }
      
      // Find the target column (where the card is being dropped)
      const targetColumn = this.columnTargets.find(column => column.dataset.status === newStatus);
      if (!targetColumn) {
        console.error("Could not find target column");
        return;
      }
      
      // Clone the card to move it to the new column
      const cardClone = cardElement.cloneNode(true);
      
      // Update the card's status attribute
      cardClone.dataset.status = newStatus;
      
      // Add a subtle styling to indicate it's a pending update
      // Just a light yellow background and border
      cardClone.classList.add('bg-yellow-50', 'border-yellow-300');
      
      // Remove the original card
      cardElement.remove();
      
      // Find the card container for the target column using the cardContainer targets
      // This works for all columns including Sold and Archived
      const targetCardContainer = this.cardContainerTargets.find(container => {
        // Find the container that belongs to the target column by checking its parent column's status
          const parentColumn = container.closest('[data-pipeline-target="column"]');
          return parentColumn && parentColumn.dataset.status === newStatus;
        });
      
      // Add the clone to the top of the target column's card container
      if (targetCardContainer && targetCardContainer.firstChild) {
        targetCardContainer.insertBefore(cardClone, targetCardContainer.firstChild);
      } else if (targetCardContainer) {
        targetCardContainer.appendChild(cardClone);
      }
      
      // Form submission will handle the loading state
      
      // Update the lead status via API
      this.updateLeadStatus(leadId, newStatus);
    } catch (error) {
      console.error("Error handling drop:", error);
    }
  }
  
  // Update lead status via form submission instead of fetch API
  updateLeadStatus(leadId, newStatus) {
    // Save current scroll position before submitting the form
    if (this.hasContainerTarget) {
      sessionStorage.setItem('pipelineScrollPosition', this.containerTarget.scrollLeft);
    }
    
    // Create a temporary form element
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = `/lead_pipelines/${newStatus}`;
    form.style.display = 'none';
    
    // Add method override for PATCH
    const methodField = document.createElement('input');
    methodField.type = 'hidden';
    methodField.name = '_method';
    methodField.value = 'PATCH';
    form.appendChild(methodField);
    
    // Add CSRF token
    const csrfField = document.createElement('input');
    csrfField.type = 'hidden';
    csrfField.name = 'authenticity_token';
    csrfField.value = document.querySelector("meta[name='csrf-token']").content;
    form.appendChild(csrfField);
    
    // Add lead ID
    const idField = document.createElement('input');
    idField.type = 'hidden';
    idField.name = 'id';
    idField.value = leadId;
    form.appendChild(idField);
    
    // Add the form to the document and submit it
    document.body.appendChild(form);
    
    // Add a class to the body to indicate we're about to reload
    // This can be used for CSS transitions if needed
    document.body.classList.add('pipeline-reloading');
    
    // Submit the form
    form.submit();
  }
}
